"use client";

import React, {useEffect, useRef, useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {
  AlertCircle,
  ArrowLeft,
  Bot,
  Info,
  Loader2,
  LogOut,
  Medal,
  Play,
  RotateCcw,
  Search,
  Sparkles,
  Swords,
  Timer,
  User,
  X
} from 'lucide-react';
import {type ClassValue, clsx} from 'clsx';
import {twMerge} from 'tailwind-merge';
import {GameHistoryItem, useGame} from '@/context/GameContext';
import {useGameRules} from '@/hooks/useGameRules';
import {RulesModal} from '@/components/RulesModal';
import {RulesTriggerIcon} from '@/components/RulesTriggerIcon';
import {FeedbackModal, FeedbackType} from './FeedbackModal';
import {FloatingSuggestButton} from './FloatingSuggestButton';
import {
  GameInput,
  GameKeyboardGrid,
  useViewportHeight
} from '@/components/keyboard/KeyboardStrategies';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL || '/api';
const INITIAL_TURN_TIME = 15;

interface Splat {
  id: string;
  word: string;
  x: number;
  y: number;
  rotate: number;
  color: string;
  scale: number;
}

function StreakBackground({ splats }: Readonly<{ splats: Splat[] }>) {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10 bg-background">
      <div className="absolute inset-0 z-10">
        <motion.svg 
          animate={{ rotate: [12, 15, 12] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-10 left-10 w-40 h-40 text-pink-500 opacity-25" viewBox="0 0 100 100"
        >
          <path d="M10,50 Q25,10 50,50 T90,50" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
        </motion.svg>
        <motion.svg 
          animate={{ rotate: [-12, -15, -12] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-20 right-10 w-52 h-52 text-sky-500 opacity-25" viewBox="0 0 100 100"
        >
          <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="6" strokeDasharray="10 8" strokeLinecap="round" />
        </motion.svg>
        <motion.svg 
          animate={{ rotate: [45, 50, 45] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 right-20 w-32 h-32 text-amber-500 opacity-25" viewBox="0 0 100 100"
        >
          <rect x="20" y="20" width="60" height="60" rx="10" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
        </motion.svg>
        <motion.svg 
          animate={{ rotate: [-6, -10, -6] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-1/4 left-20 w-36 h-36 text-emerald-500 opacity-25" viewBox="0 0 100 100"
        >
          <path d="M20,20 L80,80 M20,80 L80,20" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
        </motion.svg>
      </div>

      <AnimatePresence>
        {splats.map((splat) => (
          <motion.div
            key={splat.id}
            initial={{ scale: 0, opacity: 0, rotate: splat.rotate - 20 }}
            animate={{ scale: splat.scale, opacity: 0.15, rotate: splat.rotate }}
            exit={{ scale: 1.5, opacity: 0 }}
            transition={{ type: "spring", damping: 10, stiffness: 100 }}
            className="absolute font-doodle font-bold pointer-events-none select-none whitespace-nowrap"
            style={{ 
              color: splat.color,
              fontSize: '3rem',
              left: `${splat.x}%`,
              top: `${splat.y}%`
            }}
          >
            {splat.word.toUpperCase()}
          </motion.div>
        ))}
      </AnimatePresence>

      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{ 
          backgroundImage: 'radial-gradient(var(--border) 1px, transparent 1px)', 
          backgroundSize: '24px 24px'
        }}
      />
    </div>
  );
}

export default function GameBoard() {
  const { 
    gameState, 
    history, 
    makeMove, 
    resetGame, 
    playerId, 
    message, 
    setMessage,
    status,
    isOpponentThinking,
    playerName,
    joinAiGame,
    joinQueue,
    createCustomGame,
    serverTimeOffset,
    isOpponentDisconnected,
    rematchRequestBy,
    requestRematch,
    acceptRematch,
    declineRematch,
    refusedByPlayerId,
    customKeyboardEnabled
  } = useGame();

  useViewportHeight();

  const { isRulesOpen, hasSeenRules, openRules, closeRules } = useGameRules();

  const [input, setInput] = useState('');
  const [timeLeft, setTimeLeft] = useState(INITIAL_TURN_TIME);
  const [splats, setSplats] = useState<Splat[]>([]);
  const [selectedWordInfo, setSelectedWordInfo] = useState<{
    word: string, 
    originalWord?: string,
    insight?: string | null,
    meta: string | null,
    partOfSpeech?: string | null,
    glossary?: Record<string, string>
  } | null>(null);

  const [metadataLoading, setMetadataLoading] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackCategory, setFeedbackCategory] = useState<FeedbackType>('REPORT');
  const [feedbackInitialWord, setFeedbackInitialWord] = useState('');
  const [feedbackWordId, setFeedbackWordId] = useState<number | undefined>(undefined);

  const openFeedback = (type: FeedbackType, word: string = '', wordId?: number) => {
    setFeedbackCategory(type);
    setFeedbackInitialWord(word);
    setFeedbackWordId(wordId);
    setIsFeedbackModalOpen(true);
  };

  const [activeAbbreviation, setActiveAbbreviation] = useState<{
    text: string,
    x: number,
    y: number,
    isPrimary?: boolean,
    abbr: string
  } | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevLastWordRef = useRef<string | undefined>(undefined);

  // Drag-to-scroll logic
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const [dragDistance, setDragDistance] = useState(0);

  // Reset local state on new game
  useEffect(() => {
    if (gameState?.gameId) {
      setInput('');
      setSplats([]);
      setSelectedWordInfo(null);
      prevLastWordRef.current = undefined;
    }
  }, [gameState?.gameId]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    startXRef.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeftRef.current = scrollRef.current.scrollLeft;
    setDragDistance(0);
  };

  const handleMouseLeave = () => setIsDragging(false);
  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startXRef.current);
    setDragDistance(prev => prev + Math.abs(walk));
    scrollRef.current.scrollLeft = scrollLeftRef.current - walk;
  };

  // Sync timer with the backend deadline
  useEffect(() => {
    if (gameState?.currentTurnPlayerId && gameState.turnDeadLine && !isOpponentDisconnected) {
      const deadline = new Date(gameState.turnDeadLine).getTime();
      const updateTimer = () => {
        const nowServerTime = Date.now() + serverTimeOffset;
        const diff = Math.max(0, Math.ceil((deadline - nowServerTime) / 1000));
        setTimeLeft(diff);
      };
      updateTimer();
      const timer = setInterval(updateTimer, 1000);
      return () => clearInterval(timer);
    }
  }, [gameState?.currentTurnPlayerId, gameState?.turnDeadLine, serverTimeOffset, isOpponentDisconnected]);

  // Sync splats with history
  useEffect(() => {
    if (history.length > 0 && history[0].word !== prevLastWordRef.current) {
        prevLastWordRef.current = history[0].word;
        const colors = ['#F43F5E', '#0EA5E9', '#F59E0B', '#10B981', '#8B5CF6'];
        const newSplat: Splat = {
          id: Math.random().toString(36).slice(2, 11),
          word: history[0].word,
          x: Math.random() * 80, y: Math.random() * 80,
          rotate: Math.random() * 60 - 30,
          color: colors[Math.floor(Math.random() * colors.length)],
          scale: 0.8 + Math.random() * 0.4
        };
        setTimeout(() => {
          setSplats(prev => [newSplat, ...prev].slice(0, 100));
        }, 0);
    }
  }, [history]);

  const setNormalizedInput = (val: React.SetStateAction<string>) => {
    setInput(prev => {
      const next = typeof val === 'function' 
        ? (val as (prevState: string) => string)(prev) 
        : val;
      return next.toUpperCase();
    });
    if (message) setMessage(null);
  };

  const isMyTurn = gameState?.currentTurnPlayerId === playerId;

  // Autofocus input when it's user's turn
  useEffect(() => {
    if (isMyTurn && status === 'PLAYING' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isMyTurn, status]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || status !== 'PLAYING') return;
    makeMove(input.trim());
    setInput('');
  };

  const showWordDetail = async (item: GameHistoryItem) => {
    if (dragDistance > 10) return;
    setSelectedWordInfo({ 
      word: item.word, 
      originalWord: item.originalWord,
      insight: item.insight, 
      meta: null 
    });
    setMetadataLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/word-checker/${encodeURIComponent(item.word)}/meta`);
      const response = await res.json();
      if (response.success && response.data) {
        setSelectedWordInfo(prev => prev ? { 
          ...prev, 
          meta: response.data.meta,
          insight: response.data.info || prev.insight,
          partOfSpeech: response.data.partOfSpeech,
          glossary: response.data.glossary
        } : null);
      }
    } catch (err) {
      console.error('Failed to fetch metadata:', err);
    } finally {
      setMetadataLoading(false);
    }
  };

  const handlePlayAgain = () => {
    if (!gameState) return;
    const mode = gameState.mode;
    if (mode === 'SOLO_AI') {
      joinAiGame(playerName, true);
    } else if (mode === 'PVP_MATCHMAKING') {
      joinQueue(playerName);
    } else if (mode === 'PVP_PRIVATE_ROOM') {
      createCustomGame(playerName, true);
    }
  };

  if (!gameState) return null;

  const otherPlayer = gameState.player1.playerId === playerId ? gameState.player2 : gameState.player1;
  const myPlayer = gameState.player1.playerId === playerId ? gameState.player1 : gameState.player2;
  const hasGameStarted = gameState.status === 'IN_PROGRESS';
  const isWinner = gameState.winnerId === playerId;

  return (
    <div 
      style={{ height: 'var(--visual-vh, 100dvh)' }}
      className="text-foreground flex flex-col items-center p-2 md:p-4 font-sans selection:bg-pink-500/30 overflow-x-hidden overflow-y-auto relative transition-[height] duration-300"
    >
      <StreakBackground splats={splats} />

      <div className="w-full max-w-2xl z-10 space-y-2 md:space-y-6 pt-1 md:pt-0">
        <header className="flex items-center justify-between bg-card doodle-border p-2 md:p-6 shadow-doodle transition-all relative">
          <div className="flex items-center gap-1.5 md:gap-3">
            <RulesTriggerIcon 
              onClick={openRules} 
              hasSeenRules={hasSeenRules} 
            />
            <h1 className="text-lg md:text-3xl font-black tracking-tight handwritten">
              <span className="text-pink-500">FJALA</span>
              <span className="text-sky-500">FUNDIT</span>
            </h1>
          </div>
          
          <div className="flex items-center gap-2 md:gap-6">
            {status === 'PLAYING' && (
              <button 
                onClick={resetGame}
                className="flex items-center gap-1.5 px-2 md:px-4 py-1 md:py-2 bg-rose-50 doodle-border-soft border-rose-200 text-rose-500 hover:bg-rose-100 transition-all active:scale-95 group shadow-doodle-sm boing"
                title="Dorëzo lojën"
              >
                <LogOut className="w-3.5 h-3.5 md:w-5 md:h-5 group-hover:translate-x-0.5 transition-transform" />
                <span className="font-black handwritten text-[10px] md:text-sm tracking-normal whitespace-nowrap uppercase">DORËZO</span>
              </button>
            )}
          </div>
        </header>

        <div className="grid grid-cols-2 gap-2 md:gap-6">
          <motion.div 
            animate={{ scale: isMyTurn ? 1.02 : 1, rotate: isMyTurn ? -0.5 : 0 }}
            className={cn(
              "p-2 md:p-4 bg-card doodle-border-soft transition-all relative min-h-[60px] md:min-h-[100px] flex flex-col justify-between gap-1 md:gap-2",
              isMyTurn 
                ? "shadow-[4px_4px_0px_0px_#F43F5E] md:shadow-[6px_6px_0px_0px_#F43F5E] border-[#F43F5E] bg-rose-50" 
                : "opacity-60 shadow-doodle-sm grayscale-[0.5]"
            )}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex flex-row items-center gap-1 md:gap-2 min-w-0">
                <div className={cn("p-1 md:p-1.5 rounded-lg shrink-0", isMyTurn ? "bg-rose-400" : "bg-slate-300")}>
                  <User className="w-3.5 h-3.5 md:w-5 md:h-5 text-white" />
                </div>
                <span className="font-black text-[10px] md:text-lg truncate uppercase">TI</span>
              </div>
              {isMyTurn && (
                <div className="flex items-center justify-center gap-1 bg-card px-1 md:px-2 py-0.5 md:py-1 rounded-full border-2 border-rose-200 min-w-[38px] md:min-w-[60px] h-6 md:h-10 shadow-sm shrink-0">
                  <Timer className={cn("w-2.5 h-2.5 md:w-4 md:h-4", !hasGameStarted ? "text-slate-300" : (timeLeft <= 5 ? "text-red-500 animate-pulse" : "text-rose-400"))} />
                  <span className={cn("text-[10px] md:text-lg font-black font-mono", !hasGameStarted ? "text-slate-300" : (timeLeft <= 5 ? "text-red-500" : "text-rose-400"))}>
                    {hasGameStarted ? timeLeft : "--"}
                  </span>
                </div>
              )}
            </div>

            <div className={cn(
              "flex items-center justify-between w-full mt-1 md:mt-2 pt-1 md:pt-2 border-t border-dashed md:border-t-2",
              isMyTurn ? "border-rose-200" : "border-slate-200"
            )}>
              <div className="flex flex-col items-start">
                <div className="flex items-baseline gap-0.5 md:gap-1">
                  <span className="text-lg md:text-3xl font-black text-rose-600 tabular-nums leading-none">
                    {myPlayer?.score.points || 0}
                  </span>
                  <span className="text-[10px] md:text-[11px] font-black text-rose-400 uppercase tracking-tighter handwritten">Pikë</span>
                </div>
              </div>
              <div className="h-4 md:h-6 w-px bg-rose-100 rotate-12 mx-0.5 md:mx-1" />
              <div className="flex flex-col items-end">
                <div className="flex items-baseline gap-0.5 md:gap-1">
                  <span className="text-base md:text-2xl font-black text-rose-500/70 tabular-nums leading-none">
                    {myPlayer?.score.totalWords || 0}
                  </span>
                  <span className="text-[10px] md:text-[11px] font-black text-rose-400/50 uppercase tracking-tighter handwritten">Fjalë</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            animate={{ scale: !isMyTurn ? 1.02 : 1, rotate: !isMyTurn ? 0.5 : 0 }}
            className={cn(
              "p-2 md:p-4 bg-card doodle-border-soft transition-all relative min-h-[60px] md:min-h-[100px] flex flex-col justify-between gap-1 md:gap-2",
              !isMyTurn 
                ? "shadow-[4px_4px_0px_0px_#0EA5E9] md:shadow-[6px_6px_0px_0px_#0EA5E9] border-[#0EA5E9] bg-sky-50" 
                : "opacity-60 shadow-doodle-sm grayscale-[0.5]"
            )}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex flex-row items-center gap-1 md:gap-2 min-w-0 flex-1">
                <div className={cn("p-1 md:p-1.5 rounded-lg shrink-0", !isMyTurn ? "bg-sky-400" : "bg-slate-300")}>
                  {gameState.mode === 'SOLO_AI' ? <Bot className="w-3.5 h-3.5 md:w-5 md:h-5 text-white" /> : <User className="w-3.5 h-3.5 md:w-5 md:h-5 text-white" />}
                </div>
                <span className="font-black text-[10px] md:text-lg uppercase truncate text-left w-full">
                  {otherPlayer?.name || 'NË PRITJE...'}
                </span>
              </div>
              {!isMyTurn && (
                <div className="flex items-center justify-center gap-1 bg-card px-1 md:px-2 py-0.5 md:py-1 rounded-full border-2 border-sky-200 min-w-[38px] md:min-w-[60px] h-6 md:h-10 shadow-sm shrink-0">
                  <Timer className={cn("w-2.5 h-2.5 md:w-4 md:h-4", !hasGameStarted ? "text-slate-300" : (timeLeft <= 5 ? "text-red-500 animate-pulse" : "text-sky-400"))} />
                  <span className={cn("text-[10px] md:text-lg font-black font-mono", !hasGameStarted ? "text-slate-300" : (timeLeft <= 5 ? "text-red-500" : "text-sky-400"))}>
                    {hasGameStarted ? timeLeft : "--"}
                  </span>
                </div>
              )}
            </div>

            <div className={cn(
              "flex items-center justify-between w-full mt-1 md:mt-2 pt-1 md:pt-2 border-t border-dashed md:border-t-2",
              !isMyTurn ? "border-sky-200" : "border-slate-200"
            )}>
              <div className="flex flex-col items-start">
                <div className="flex items-baseline gap-0.5 md:gap-1">
                  <span className="text-lg md:text-3xl font-black text-sky-600 tabular-nums leading-none">
                    {otherPlayer?.score.points || 0}
                  </span>
                  <span className="text-[10px] md:text-[11px] font-black text-sky-400 uppercase tracking-tighter handwritten">Pikë</span>
                </div>
              </div>
              <div className="h-4 md:h-6 w-px bg-sky-100 rotate-12 mx-0.5 md:mx-1" />
              <div className="flex flex-col items-end">
                <div className="flex items-baseline gap-0.5 md:gap-1">
                  <span className="text-base md:text-2xl font-black text-sky-500/70 tabular-nums leading-none">
                    {otherPlayer?.score.totalWords || 0}
                  </span>
                  <span className="text-[10px] md:text-[11px] font-black text-sky-400/50 uppercase tracking-tighter handwritten">Fjalë</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <main className="relative bg-card doodle-border p-3 md:p-12 shadow-doodle-lg flex flex-col items-center justify-center min-h-[220px] md:min-h-[420px] w-full overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(var(--border) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />

          <AnimatePresence mode="wait">
            {!gameState.lastPlayedWord ? (
              <motion.div key="start" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center space-y-2 md:space-y-4">
                <div className="text-pink-500 font-bold uppercase tracking-widest text-[10px] md:text-sm marker-highlight handwritten">Gati?</div>
                <div className="text-2xl md:text-5xl font-black text-foreground leading-tight handwritten">Shkruaj fjalën e parë!</div>
              </motion.div>
            ) : (
              <motion.div key={gameState.lastPlayedWord} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-center w-full px-1 md:px-2">
                <div className="text-sky-500 font-bold uppercase tracking-widest text-[8px] md:text-xs mb-2 md:mb-4 opacity-60">Fjala në lojë</div>
                <div className={cn("font-black tracking-tight break-all leading-[1.1] handwritten", 
                  gameState.lastPlayedWord.length > 12 ? "text-3xl md:text-5xl" : 
                  gameState.lastPlayedWord.length > 8 ? "text-4xl md:text-5xl" :
                  "text-5xl md:text-6xl")}>
                  {(() => {
                    const word = gameState.lastPlayedWord;
                    const req = gameState.requiredStartLetter || '';
                    const isDigraph = req.length === 2;
                    const splitPoint = word.length - (isDigraph ? 2 : 1);

                    return word.split('').map((char, i) => {
                      const isTarget = i >= splitPoint;
                      return (
                        <span key={i} className={cn(isTarget ? "text-pink-500 drop-shadow-[1px_1px_0px_var(--border)] md:drop-shadow-[2px_2px_0px_var(--border)]" : "text-foreground")}>
                          {char.toUpperCase()}
                        </span>
                      );
                    });
                  })()}
                </div>

                <div className="mt-4 md:mt-10 flex flex-col items-center justify-center gap-2 md:gap-4">
                  {isMyTurn ? (
                    <motion.div 
                      initial={{ scale: 0.9, opacity: 0 }} 
                      animate={{ scale: 1, opacity: 1 }}
                      className="flex flex-col items-center gap-1 md:gap-3"
                    >
                      <div className="text-rose-500 font-black text-lg md:text-xl handwritten animate-wiggle uppercase tracking-wider">Radha juaj!</div>
                      <div className="flex items-center justify-center gap-2 md:gap-3">
                        <span className="text-slate-400 font-bold text-[10px] md:text-sm handwritten">Fillon me:</span>
                        <span className="bg-amber-400 doodle-border-soft text-white px-2.5 md:px-4 py-0.5 md:py-1.5 text-lg md:text-2xl font-black shadow-doodle-sm">
                          {(gameState.requiredStartLetter || '').toUpperCase()}
                        </span>
                      </div>
                    </motion.div>
                  ) : (
                    <div className="flex flex-col items-center gap-1 md:gap-3">
                      {isOpponentThinking ? (
                        <div className="flex items-center gap-2 text-sky-500 animate-bounce">
                          <Sparkles className="w-4 h-4 md:w-5 md:h-5" />
                          <span className="font-black text-lg md:text-xl handwritten uppercase tracking-tight">
                            {gameState.mode === 'SOLO_AI' ? 'GJENIU' : otherPlayer?.name} po mendon...
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1 md:gap-3 opacity-80">
                          <div className="text-sky-400 font-black text-base md:text-lg handwritten uppercase">
                            Radha e {gameState.mode === 'SOLO_AI' ? 'GJENIUT' : otherPlayer?.name}...
                          </div>
                          <div className="flex items-center justify-center gap-2 md:gap-3 opacity-40 grayscale">
                            <span className="text-slate-400 font-bold text-[10px] md:text-sm handwritten">Fillon me:</span>
                            <span className="bg-slate-200 doodle-border-soft text-slate-500 px-2 md:px-3 py-0.5 md:py-1 text-lg md:text-xl font-black">
                              {(gameState.requiredStartLetter || '').toUpperCase()}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {status === 'GAMEOVER' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 bg-card/95 backdrop-blur-md flex flex-col items-center justify-center p-3 z-20">
                <div className="flex items-center gap-2 mb-3 w-full max-w-[280px] justify-center">
                  <div className={cn(
                    "p-1 md:p-1.5 doodle-border-soft shrink-0 shadow-doodle-sm",
                    isWinner ? "bg-amber-100 text-amber-500" : "bg-pink-100 text-pink-500"
                  )}>
                    {isWinner ? <Medal className="w-3.5 h-3.5 md:w-4 md:h-4" /> : <AlertCircle className="w-3.5 h-3.5 md:w-4 md:h-4" />}
                  </div>
                  <div className={cn(
                    "p-2 doodle-border-soft rotate-1 shadow-doodle-sm flex-1",
                    isWinner ? "bg-amber-50 border-amber-200" : "bg-rose-50 border-rose-200"
                  )}>
                    <p className={cn(
                      "font-black handwritten text-[10px] md:text-base leading-tight text-center",
                      isWinner ? "text-amber-600" : "text-rose-500"
                    )}>{message?.text}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 w-full mb-4 max-w-[280px] md:max-w-sm">
                  <div className="bg-rose-50 p-1.5 md:p-3 doodle-border-soft border-rose-200 -rotate-2">
                    <div className="text-[7px] md:text-[9px] font-black text-rose-400 uppercase tracking-widest handwritten text-center">TI</div>
                    <div className="flex flex-col items-center">
                      <div className="text-base md:text-2xl font-black text-rose-600 leading-none">{myPlayer?.score.points || 0}</div>
                      <div className="text-[8px] md:text-sm font-black text-rose-500/60 handwritten">{myPlayer?.score.totalWords || 0} fjalë</div>
                    </div>
                  </div>
                  <div className="bg-sky-50 p-1.5 md:p-3 doodle-border-soft border-sky-200 rotate-1">
                    <div className="text-[7px] md:text-[9px] font-black text-sky-400 uppercase tracking-widest handwritten text-center uppercase truncate w-full">{otherPlayer?.name || '...'}</div>
                    <div className="flex flex-col items-center">
                      <div className="text-base md:text-2xl font-black text-sky-600 leading-none">{otherPlayer?.score.points || 0}</div>
                      <div className="text-[8px] md:text-sm font-black text-sky-500/60 handwritten">{otherPlayer?.score.totalWords || 0} fjalë</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </main>

        <div className="space-y-4">
          {status === 'GAMEOVER' ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full"
            >
              {!(gameState.mode === 'PVP_PRIVATE_ROOM' && gameState.winningReason === 'RESIGNED') ? (
                <div className="w-full">
                  {rematchRequestBy && rematchRequestBy !== playerId ? (
                    <div className="bg-card doodle-border p-4 md:p-6 shadow-[8px_8px_0px_0px_var(--border)] flex flex-col items-center gap-4">
                      <div className="bg-amber-400 text-white px-6 py-2 doodle-border rotate-[-2deg] font-black text-xl md:text-2xl handwritten shadow-[4px_4px_0px_0px_var(--border)] uppercase">
                        SFIDË E RE??
                      </div>
                      <p className="font-black text-foreground handwritten text-center">
                        Kundërshtari po të pret për një tjetër lojë!
                      </p>
                      <div className="flex flex-row gap-3 w-full">
                        <button 
                          onClick={declineRematch}
                          className="flex-1 bg-card text-rose-500 py-3 doodle-border shadow-[4px_4px_0px_0px_#F43F5E] font-black text-xl hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all active:scale-95 boing"
                        >
                          JO
                        </button>
                        <button 
                          onClick={acceptRematch}
                          className="flex-1 bg-[var(--border)] text-white py-3 doodle-border shadow-[4px_4px_0px_0px_#10B981] font-black text-xl hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all active:scale-95 boing"
                        >
                          PO!
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-row gap-2 md:gap-3 w-full">
                      <button 
                        onClick={resetGame} 
                        className="flex-1 flex items-center justify-center gap-1.5 md:gap-2 bg-rose-500 text-white py-3 md:py-5 doodle-border shadow-[4px_4px_0px_0px_var(--border)] md:shadow-[6px_6px_0px_0px_var(--border)] font-black text-[11px] sm:text-sm md:text-xl hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all active:scale-95 boing"
                      >
                        <ArrowLeft className="w-4 h-4 md:w-6 md:h-6" /> KTHEHU NË LOB
                      </button>
                      <button 
                        onClick={gameState.mode === 'PVP_PRIVATE_ROOM' ? requestRematch : handlePlayAgain} 
                        disabled={rematchRequestBy === playerId || !!refusedByPlayerId}
                        className={cn(
                          "flex-1 flex items-center justify-center gap-1.5 md:gap-2 text-white py-3 md:py-5 doodle-border shadow-[4px_4px_0px_0px_var(--border)] md:shadow-[6px_6px_0px_0px_var(--border)] font-black text-[11px] sm:text-sm md:text-xl transition-all active:scale-95 boing",
                          (rematchRequestBy === playerId || !!refusedByPlayerId) ? "bg-slate-400 cursor-default" : "bg-sky-400 hover:translate-x-[-2px] hover:translate-y-[-2px]"
                        )}
                      >
                        {rematchRequestBy === playerId ? (
                          <>
                            <Loader2 className="w-4 h-4 md:w-6 md:h-6 animate-spin" />
                            <span>DUKE PRITUR...</span>
                          </>
                        ) : !!refusedByPlayerId ? (
                           <>
                            <X className="w-4 h-4 md:w-6 md:h-6" /> 
                            <span>ANULUAR</span>
                          </>
                        ) : (
                          <>
                            <RotateCcw className="w-4 h-4 md:w-6 md:h-6" /> 
                            <span>LUAJ PËRSËRI</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button 
                  onClick={resetGame} 
                  className="w-full flex items-center justify-center gap-2 bg-rose-500 text-white py-4 md:py-5 doodle-border shadow-[6px_6px_0px_0px_var(--border)] font-black text-lg md:text-xl hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all active:scale-95 boing"
                >
                  <ArrowLeft className="w-5 h-5 md:w-6 md:h-6" /> KTHEHU NË LOB
                </button>
              )}
            </motion.div>
          ) : (
            <div className="space-y-4">
              <GameInput 
                input={input} 
                setInput={setNormalizedInput} 
                handleSubmit={handleSubmit} 
                isMyTurn={isMyTurn} 
                placeholder={!isMyTurn ? "Prisni radhën..." : (!gameState.lastPlayedWord ? "Shkruaj fjalën e parë..." : `Fjalë me "${(gameState.requiredStartLetter || '').toUpperCase()}"...`)}
                isNative={!customKeyboardEnabled}
                inputRef={inputRef}
              />
              
              {customKeyboardEnabled && (
                <GameKeyboardGrid
                  input={input}
                  setInput={setNormalizedInput}
                  handleSubmit={handleSubmit}
                  isMyTurn={isMyTurn}
                />
              )}
            </div>
          )}

          <AnimatePresence mode="wait">
            {message && (
              <motion.div 
                initial={{ opacity: 0, y: 10, x: 0 }} 
                animate={{ 
                  opacity: 1, 
                  y: 0,
                  x: message.type === 'error' ? [0, -10, 10, -10, 10, 0] : 0
                }} 
                transition={{
                  x: { duration: 0.4, ease: "easeInOut" }
                }}
                className={cn(
                  "p-3 doodle-border-soft border-2 font-black handwritten text-sm md:text-lg", 
                  message.type === 'error' ? "bg-rose-50 border-rose-200 text-rose-500" : 
                  message.type === 'success' ? "bg-emerald-50 border-emerald-200 text-emerald-600" : 
                  "bg-sky-50 border-sky-200 text-sky-500"
                )}
              >
                {message.text}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="space-y-2 md:space-y-4 pt-2 md:pt-4">
          <div className="flex items-center justify-between px-2">
            <h3 className="text-slate-400 font-black uppercase tracking-widest text-[10px] md:text-xs handwritten">Fjalët e Fundit</h3>
            <div className="flex items-center gap-2">
              <span className="text-[8px] md:text-[10px] text-slate-300 font-bold uppercase handwritten animate-pulse">Rrëshqit ➔</span>
              <span className="text-[10px] md:text-xs bg-card/80 backdrop-blur-md doodle-border-soft px-2 md:px-3 py-0.5 md:py-1 font-black shadow-[2px_2px_0px_0px_var(--border)]">{history.length} total</span>
            </div>
          </div>
          
          <div className="relative group pb-4 md:pb-8">
            <div 
              ref={scrollRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              className={cn(
                "flex gap-3 md:gap-5 overflow-x-auto pb-4 md:pb-8 pt-1 md:pt-2 px-2 no-scrollbar min-h-[60px] md:min-h-[80px] select-none",
                isDragging ? "cursor-grabbing snap-none" : "cursor-grab snap-x"
              )}
            >
              {history.length === 0 && (
                <div className="text-slate-300 font-black handwritten italic text-base md:text-lg opacity-40 px-2">Asnjë fjalë ende...</div>
              )}
              {history.map((item, i) => (
                <motion.div
                  key={`${item.word}-${i}`}
                  onClick={() => showWordDetail(item)}
                  initial={{ opacity: 0, scale: 0.8, x: 50 }}
                  animate={{ 
                    opacity: 1, 
                    scale: 1, 
                    x: 0,
                    rotate: (i % 2 === 0 ? 1.5 : -1.5) 
                  }}
                  whileHover={{ 
                    scale: 1.05, 
                    rotate: 0,
                    y: -5,
                    transition: { type: "spring", stiffness: 400, damping: 10 }
                  }}
                  whileTap={{ scale: 0.95 }}
                  className={cn(
                    "flex-shrink-0 flex items-center gap-2 md:gap-3 px-4 md:px-6 py-2 md:py-4 doodle-border-soft shadow-[3px_3px_0px_0px_var(--border)] md:shadow-[5px_5px_0px_0px_var(--border)] font-black text-lg md:text-xl handwritten snap-center cursor-pointer transition-colors backdrop-blur-md relative",
                    item.playerId === playerId ? "bg-rose-50/90 text-rose-500 border-rose-200 hover:bg-rose-100/90" : "bg-sky-50/90 text-sky-500 border-sky-200 hover:bg-sky-100/90"
                  )}
                >
                  <span className="relative flex items-center gap-1.5 md:gap-2">
                    {item.originalWord ? (
                      <span className="flex items-center gap-1 md:gap-1.5">
                        <span className="opacity-40 text-sm md:text-lg">{item.originalWord.toUpperCase()}</span>
                        <span className="text-slate-300 text-xs md:text-base">→</span>
                        <span className="text-sm md:text-lg">{item.word.toUpperCase()}*</span>
                        <Sparkles className="w-3 h-3 md:w-4 md:h-4 text-amber-400 animate-pulse" />
                      </span>
                    ) : (
                      item.word.toUpperCase()
                    )}
                    {item.playerId === playerId && (
                      <div className="absolute -top-1 -right-1 w-1.5 h-1.5 md:w-2 md:h-2 bg-rose-400 rounded-full animate-ping" />
                    )}
                  </span>
                  <div className="flex items-center gap-1 md:gap-1.5">
                    <span className="text-[10px] md:text-xs px-1 md:px-1.5 py-0.5 rounded-md bg-black/5 opacity-30">
                      +{item.score}
                    </span>
                    <Info className="w-3 h-3 md:w-4 md:h-4 opacity-20" />
                  </div>
                </motion.div>
              ))}

              <div className="flex-shrink-0 w-4" />
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {selectedWordInfo && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-lg bg-card doodle-border p-5 md:p-12 shadow-doodle-xl overflow-hidden"
            >
              <button 
                onClick={() => setSelectedWordInfo(null)}
                className="absolute top-3 md:top-4 right-3 md:right-4 p-2 hover:bg-slate-100 rounded-full transition-colors boing"
              >
                <X className="w-5 h-5 md:w-6 md:h-6 text-slate-400" />
              </button>

              <div className="space-y-4 md:space-y-6 pt-2 md:pt-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1 min-w-0 flex-1">
                    <div className="text-pink-500 font-black uppercase tracking-widest text-[10px] md:text-xs handwritten">Kuptimi i fjalës</div>
                    <h2 className="text-2xl md:text-4xl font-black text-foreground handwritten drop-shadow-sm break-all leading-tight">
                      {selectedWordInfo.word.toUpperCase()}
                    </h2>
                  </div>
                  <button 
                    onClick={() => {
                      const wordToReport = selectedWordInfo.word;
                      setSelectedWordInfo(null);
                      openFeedback('REPORT', wordToReport);
                    }}
                    className="flex flex-col items-center gap-1 group shrink-0"
                  >
                    <div className="p-2 md:p-3 bg-rose-50 border-2 border-rose-100 rounded-2xl group-hover:bg-rose-500 group-hover:text-white group-hover:border-rose-600 transition-all shadow-sm">
                      <AlertCircle className="w-4 h-4 md:w-5 md:h-5" />
                    </div>
                    <span className="text-[8px] md:text-[10px] font-black text-rose-400 uppercase tracking-tighter handwritten group-hover:text-rose-600">Raporto</span>
                  </button>
                </div>

                {selectedWordInfo.insight && !selectedWordInfo.insight.includes("është trajtë e fjalës") && (
                  <div className="bg-sky-50 p-3 md:p-4 doodle-border-soft border-sky-200 -rotate-1 shadow-doodle-sm md:shadow-[4px_4px_0px_0px_#0EA5E9]">
                    <div className="flex items-start gap-2.5 md:gap-3">
                      <div className="p-1 md:p-1.5 bg-sky-400 rounded-lg shrink-0">
                        <Sparkles className="w-3.5 h-3.5 md:w-4 md:h-4 text-white" />
                      </div>
                      <div className="space-y-0.5 md:space-y-1">
                        <div className="text-[9px] md:text-[10px] font-black text-sky-600 uppercase tracking-widest handwritten">Këshillë gjuhësore</div>
                        <p className="text-sky-700 font-bold handwritten text-xs md:text-sm leading-snug">
                          {selectedWordInfo.insight}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {selectedWordInfo.insight && selectedWordInfo.insight.includes("është trajtë e fjalës") && (
                  <div className="bg-amber-50 p-3 md:p-4 doodle-border-soft border-amber-200 rotate-1 shadow-doodle-sm md:shadow-[4px_4px_0px_0px_#F59E0B]">
                    <div className="flex items-start gap-2.5 md:gap-3">
                      <div className="p-1 md:p-1.5 bg-amber-400 rounded-lg shrink-0">
                        <Info className="w-3.5 h-3.5 md:w-4 md:h-4 text-white" />
                      </div>
                      <div className="space-y-0.5 md:space-y-1">
                        <div className="text-[9px] md:text-[10px] font-black text-amber-600 uppercase tracking-widest handwritten">Shënim gjuhësor</div>
                        <p className="text-amber-800 font-bold handwritten text-xs md:text-sm leading-snug italic">
                          &quot;{selectedWordInfo.insight}&quot;
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-muted p-4 md:p-6 doodle-border-soft border-slate-200 min-h-[250px] md:min-h-[300px] relative max-h-[40vh] overflow-y-auto no-scrollbar">
                   {metadataLoading ? (
                     <div className="absolute inset-0 flex flex-col items-center justify-center space-y-3 bg-muted/80 backdrop-blur-[1px] z-10">
                        <motion.div
                          animate={{ 
                            rotate: [0, 10, -10, 0],
                            scale: [1, 1.1, 1],
                            y: [0, -5, 0]
                          }}
                          transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                          className="bg-pink-400 p-3 rounded-2xl shadow-[4px_4px_0px_0px_#db2777]"
                        >
                          <Search className="w-6 h-6 md:w-8 md:h-8 text-white" />
                        </motion.div>
                        <motion.p 
                          animate={{ opacity: [0.4, 1, 0.4] }}
                          transition={{ repeat: Infinity, duration: 1.5 }}
                          className="font-black handwritten text-pink-500 text-sm md:text-base uppercase tracking-widest"
                        >
                          Duke kërkuar...
                        </motion.p>
                     </div>
                   ) : selectedWordInfo.meta ? (
                     <motion.div layout className="text-foreground font-bold handwritten text-base md:text-lg leading-relaxed whitespace-pre-line text-left">
                       {(() => {
                         if (!selectedWordInfo.glossary) return selectedWordInfo.meta;
                         const text = selectedWordInfo.meta;
                         const glossary = selectedWordInfo.glossary;
                         const glossaryKeys = Object.keys(glossary).sort((a, b) => b.length - a.length);
                         return text.split(/(\s+)/).map((token, index) => {
                           const trimmedToken = token.trim();
                           if (!trimmedToken) return token;
                           const abbrKey = glossaryKeys.find(key => trimmedToken === key || trimmedToken.startsWith(key));
                           const fullMeaning = abbrKey ? glossary[abbrKey] : null;
                           if (abbrKey && fullMeaning) {
                             const isPrimaryPos = selectedWordInfo.partOfSpeech && fullMeaning.toLowerCase().includes(selectedWordInfo.partOfSpeech.toLowerCase());
                             const isExpanded = activeAbbreviation?.abbr === abbrKey;
                             return (
                               <motion.button
                                 key={index}
                                 layout
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   if (isExpanded) {
                                     setActiveAbbreviation(null);
                                     return;
                                   }
                                   setActiveAbbreviation({ abbr: abbrKey, text: fullMeaning, x: 0, y: 0, isPrimary: !!isPrimaryPos });
                                 }}
                                 className={`inline-flex items-center overflow-hidden transition-all align-baseline mx-0.5 rounded-lg ${isExpanded ? (isPrimaryPos ? 'bg-pink-500 text-white shadow-md' : 'bg-[var(--border)] text-white shadow-md') : (isPrimaryPos ? 'bg-pink-100 text-pink-600 border-2 border-pink-200' : 'bg-sky-50 text-sky-600 border-b-2 border-dotted border-sky-300 hover:bg-sky-100')}`}
                                 style={{ padding: isExpanded ? '2px 10px' : '0px 4px', height: '24px', position: 'relative', top: '2px' }}
                               >
                                 <motion.span layout="position" className="font-black whitespace-nowrap">{token}</motion.span>
                                 <AnimatePresence mode="popLayout">
                                   {isExpanded && (
                                     <motion.span initial={{ width: 0, opacity: 0, x: -5 }} animate={{ width: 'auto', opacity: 1, x: 0 }} exit={{ width: 0, opacity: 0, x: -5 }} className="flex items-center gap-2 ml-2 border-l border-white/20 pl-2 py-0.5">
                                       <span className="text-[10px] md:text-xs font-bold whitespace-nowrap uppercase tracking-tighter opacity-95">{fullMeaning}</span>
                                     </motion.span>
                                   )}
                                 </AnimatePresence>
                               </motion.button>
                             );
                           }
                           return token;
                         });
                       })()}
                     </motion.div>
                   ) : (
                     <div className="flex flex-col items-center justify-center text-center space-y-2 md:space-y-3 opacity-40 py-6 md:py-8">
                       <AlertCircle className="w-8 h-8 md:w-10 md:h-10" />
                       <p className="font-black handwritten text-base md:text-lg">Nuk u gjet asnjë kuptim.</p>
                     </div>
                   )}
                </div>

                <div className="flex justify-end pt-1 md:pt-2">
                   <button
                    onClick={() => setSelectedWordInfo(null)}
                    className="bg-pink-400 text-white px-6 md:px-8 py-2 md:py-3 doodle-border-soft shadow-doodle-sm md:shadow-doodle font-black text-base md:text-lg hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all active:scale-95 boing"
                   >
                     E KUPTOVA
                   </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <RulesModal isOpen={isRulesOpen} onClose={closeRules} />

      <AnimatePresence>
        {rematchRequestBy && rematchRequestBy !== playerId && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.5, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.5, opacity: 0, y: 20 }} className="bg-card doodle-border p-8 md:p-12 shadow-doodle-xl flex flex-col items-center gap-8 max-w-sm w-full relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-20"><Swords size={40} className="text-amber-400" /></div>
              <div className="space-y-4 text-center">
                <div className="inline-block bg-amber-400 text-white px-6 py-2 doodle-border rotate-[-3deg] font-black text-2xl md:text-3xl handwritten shadow-[4px_4px_0px_0px_var(--border)] uppercase">SFIDË E RE?</div>
                <p className="font-black text-foreground handwritten text-xl leading-snug">Kundërshtari po të pret për një tjetër lojë!</p>
              </div>
              <div className="flex flex-col w-full gap-4">
                <button onClick={acceptRematch} className="w-full bg-[var(--border)] text-white py-5 doodle-border shadow-[6px_6px_0px_0px_#10B981] font-black text-2xl hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all active:scale-95 boing flex items-center justify-center gap-3">
                  <Play className="w-8 h-8 fill-current" /> PO, FILLOJMË!
                </button>
                <button onClick={declineRematch} className="w-full bg-card text-rose-500 py-4 doodle-border shadow-[4px_4px_0px_0px_#F43F5E] font-black text-xl hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all active:scale-95 boing">JO, FALEMINDERIT</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="fixed bottom-6 right-6 z-40">
        <FloatingSuggestButton onClick={() => openFeedback('SUGGESTION')} />
      </div>

      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        type={feedbackCategory}
        initialWord={feedbackInitialWord}
        wordId={feedbackWordId}
      />
    </div>
  );
}
